import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { DataTable, FinanceCard, NativeSelect, money } from "./financeUi";

const PARTY_TYPES = ["CUSTOMER", "VENDOR", "EXPERT", "SOCIETY", "OTHER"];

export default function FinanceMasters({ accounts, parties, items, units, reload }) {
  return (
    <Tabs defaultValue="parties">
      <TabsList className="mb-5">
        <TabsTrigger data-testid="masters-parties-tab" value="parties">Parties ({parties.length})</TabsTrigger>
        <TabsTrigger data-testid="masters-items-tab" value="items">Items / Services ({items.length})</TabsTrigger>
        <TabsTrigger data-testid="masters-units-tab" value="units">Units ({units.length})</TabsTrigger>
      </TabsList>
      <TabsContent value="parties"><PartyMaster parties={parties} reload={reload} /></TabsContent>
      <TabsContent value="items"><ItemMaster accounts={accounts} items={items} reload={reload} units={units} /></TabsContent>
      <TabsContent value="units"><UnitMaster reload={reload} units={units} /></TabsContent>
    </Tabs>
  );
}

const save = async (path, body, label, reload, reset) => {
  try {
    await api.post(path, body);
    toast.success(`${label} saved`);
    reset();
    reload();
  } catch (e) {
    toast.error(e.response?.data?.detail || `Could not save ${label.toLowerCase()}`);
  }
};

function PartyMaster({ parties, reload }) {
  const blank = { name: "", type: "CUSTOMER", contact_person: "", phone: "", email: "", gstin: "", address: "", opening_balance: "" };
  const [form, setForm] = useState(blank);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <FinanceCard testid="party-form" title="New party">
        <Input data-testid="party-name-input" onChange={set("name")} placeholder="Party name" value={form.name} />
        <NativeSelect data-testid="party-type-select" onChange={set("type")} value={form.type}>
          {PARTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </NativeSelect>
        <Input data-testid="party-contact-input" onChange={set("contact_person")} placeholder="Contact person" value={form.contact_person} />
        <Input data-testid="party-phone-input" onChange={set("phone")} placeholder="Phone" value={form.phone} />
        <Input data-testid="party-email-input" onChange={set("email")} placeholder="Email" value={form.email} />
        <Input data-testid="party-gstin-input" onChange={set("gstin")} placeholder="GSTIN (optional)" value={form.gstin} />
        <Input data-testid="party-address-input" onChange={set("address")} placeholder="Address" value={form.address} />
        <Input data-testid="party-opening-input" onChange={set("opening_balance")} placeholder="Opening balance (receivable)" type="number" value={form.opening_balance} />
        <Button data-testid="party-save-button" onClick={() => save("/admin/company-finance/parties", { ...form, opening_balance: Number(form.opening_balance || 0) }, "Party", reload, () => setForm(blank))}>
          <Plus className="w-4 h-4" />Save Party
        </Button>
      </FinanceCard>
      <DataTable
        columns={[
          ["Party", (p) => <span className="font-medium">{p.name}</span>],
          ["Type", (p) => <span className="text-xs rounded bg-muted px-2 py-0.5">{p.type}</span>],
          ["Contact", (p) => p.contact_person || p.phone || p.email || "—"],
          ["GSTIN", (p) => p.gstin || "—"],
          ["Opening", (p) => money(p.opening_balance), "right"],
        ]}
        empty="No parties yet. Add customers, vendors, Experts or Societies here."
        rowKey={(p) => p.party_id}
        rows={parties}
        testid="party-table"
      />
    </div>
  );
}

function ItemMaster({ accounts, items, units, reload }) {
  const incomeAccounts = accounts.filter((a) => a.type === "income");
  const expenseAccounts = accounts.filter((a) => a.type === "expense");
  const blank = { name: "", kind: "SERVICE", sku: "", unit: "", rate: "", tax_pct: "", income_account: "Other Income", expense_account: "" };
  const [form, setForm] = useState(blank);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <FinanceCard testid="item-form" title="New item / service">
        <Input data-testid="item-name-input" onChange={set("name")} placeholder="Item or service name" value={form.name} />
        <NativeSelect data-testid="item-kind-select" onChange={set("kind")} value={form.kind}>
          <option value="SERVICE">Service</option>
          <option value="ITEM">Item / Material</option>
        </NativeSelect>
        <Input data-testid="item-sku-input" onChange={set("sku")} placeholder="Code / SKU (optional)" value={form.sku} />
        <NativeSelect data-testid="item-unit-select" onChange={set("unit")} value={form.unit}>
          <option value="">Unit (optional)</option>
          {units.map((u) => <option key={u.unit_id} value={u.symbol}>{u.name} ({u.symbol})</option>)}
        </NativeSelect>
        <Input data-testid="item-rate-input" onChange={set("rate")} placeholder="Default rate" type="number" value={form.rate} />
        <Input data-testid="item-tax-input" onChange={set("tax_pct")} placeholder="Tax % (e.g. 18)" type="number" value={form.tax_pct} />
        <NativeSelect data-testid="item-income-select" onChange={set("income_account")} value={form.income_account}>
          {incomeAccounts.map((a) => <option key={a.account_id} value={a.name}>{a.code} · {a.name}</option>)}
        </NativeSelect>
        <NativeSelect data-testid="item-expense-select" onChange={set("expense_account")} value={form.expense_account}>
          <option value="">Expense account (optional)</option>
          {expenseAccounts.map((a) => <option key={a.account_id} value={a.name}>{a.code} · {a.name}</option>)}
        </NativeSelect>
        <Button data-testid="item-save-button" onClick={() => save("/admin/company-finance/items", { ...form, rate: Number(form.rate || 0), tax_pct: Number(form.tax_pct || 0) }, "Item", reload, () => setForm(blank))}>
          <Plus className="w-4 h-4" />Save Item
        </Button>
      </FinanceCard>
      <DataTable
        columns={[
          ["Item", (i) => <span className="font-medium">{i.name}</span>],
          ["Kind", (i) => <span className="text-xs rounded bg-muted px-2 py-0.5">{i.kind}</span>],
          ["Unit", (i) => i.unit || "—"],
          ["Rate", (i) => money(i.rate), "right"],
          ["Tax %", (i) => `${i.tax_pct || 0}%`, "right"],
          ["Income account", (i) => i.income_account],
        ]}
        empty="No items or services yet."
        rowKey={(i) => i.item_id}
        rows={items}
        testid="item-table"
      />
    </div>
  );
}

function UnitMaster({ units, reload }) {
  const [form, setForm] = useState({ name: "", symbol: "" });
  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <FinanceCard testid="unit-form" title="New unit">
        <Input data-testid="unit-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Unit name (e.g. Numbers, Hours, Sq. ft.)" value={form.name} />
        <Input data-testid="unit-symbol-input" onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="Symbol (e.g. Nos, Hr, Sqft)" value={form.symbol} />
        <Button data-testid="unit-save-button" onClick={() => save("/admin/company-finance/units", form, "Unit", reload, () => setForm({ name: "", symbol: "" }))}>
          <Plus className="w-4 h-4" />Save Unit
        </Button>
      </FinanceCard>
      <DataTable
        columns={[["Unit", (u) => u.name], ["Symbol", (u) => <span className="font-mono">{u.symbol}</span>]]}
        empty="No units yet."
        rowKey={(u) => u.unit_id}
        rows={units}
        testid="unit-table"
      />
    </div>
  );
}
